import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { ArrowUpRight, MapPin, Compass, Sparkles, Trophy, Languages, Lock, UtensilsCrossed, ScrollText, Handshake, KeyRound } from "lucide-react";
import { absUrl } from "@/components/PhotoUploader";
import { useLang } from "@/lib/i18n";

/**
 * Auto-generated SEO landing page per city. Injects meta tags directly on
 * mount so search engines (and rich-preview crawlers like Twitter/OG) get
 * a proper title + description + og:image + JSON-LD structured data.
 *
 * URL: /citta/:city
 */
export default function CityPage({ citySlug }) {
  // Accept `citySlug` prop so TreasureHunt can render this page inline for
  // /caccia-al-tesoro/{city-slug} URLs without a redirect. Falls back to the
  // route param for the direct /citta/:city entry.
  const params = useParams();
  const city = (citySlug || params.city || "").toString();
  const { lang, setLang } = useLang();
  const IT = lang === "it";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Inline mini-preview: 3 teaser tappe fetched from /hunts/{id}. The full
  // interactive map is intentionally NOT reachable from here — the button
  // toggles this small taste rather than navigating away, because
  // "anteprima" ≠ intera mappa (feedback 2026-08-26).
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewStops, setPreviewStops] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const cityTitle = city.charAt(0).toUpperCase() + city.slice(1);

  useEffect(() => {
    if (!city) return;
    setLoading(true);
    api.get(`/city/${encodeURIComponent(city)}/summary${lang && lang !== "it" ? `?lang=${lang}` : ""}`)
      .then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [city, lang]);

  useEffect(() => {
    if (!data) return;
    const h = data.hunts?.[0];
    const editorialFirst = (data.editorial?.paragraphs?.[0] || "").slice(0, 155);
    // SEO-tuned title mixing (1) primary keyword "Cosa fare a {city}",
    // (2) the hunt name when available, and (3) the room-count so the
    // snippet on Google shows both the destination + inventory.
    const title = h
      ? (IT
          ? `Cosa fare a ${cityTitle}: ${h.title} + ${data.properties_total} strutture | HospitalityHub`
          : `What to do in ${cityTitle}: ${h.title} + ${data.properties_total} stays | HospitalityHub`)
      : (IT
          ? `Cosa fare a ${cityTitle}: B&B, hotel e itinerari 2026 | HospitalityHub`
          : `What to do in ${cityTitle}: B&Bs, hotels and itineraries 2026 | HospitalityHub`);
    // Description prefers the AI-generated first paragraph when present so
    // Google sees genuine editorial copy instead of a boilerplate template.
    const desc = editorialFirst
      ? editorialFirst
      : (h
        ? `${h.subtitle || h.description || ""} · ${data.properties_total} ${IT ? "strutture ricettive a" : "stays in"} ${cityTitle}.`.trim()
        : (IT
            ? `Trova ${data.properties_total} B&B, hotel e case vacanza a ${cityTitle}. Prenota direttamente dall'host.`
            : `Find ${data.properties_total} B&Bs, hotels and vacation rentals in ${cityTitle}. Book directly with the host.`));
    const ogImg = h?.hero_image || data.properties?.[0]?.hero_image || data.properties?.[0]?.photos?.[0];
    document.title = title;
    setMeta("description", desc);
    setMeta("keywords", IT
      ? `${cityTitle}, cosa fare a ${cityTitle}, caccia al tesoro ${cityTitle}, B&B ${cityTitle}, hotel ${cityTitle}, itinerario ${cityTitle}, monumenti ${cityTitle}, ristoranti tipici ${cityTitle}`
      : `${cityTitle}, what to do in ${cityTitle}, treasure hunt ${cityTitle}, B&B ${cityTitle}, hotel ${cityTitle}, itinerary ${cityTitle}, landmarks ${cityTitle}, local food ${cityTitle}`);
    setMeta("og:locale", IT ? "it_IT" : "en_US", true);
    setMeta("og:title", title, true);
    setMeta("og:description", desc, true);
    setMeta("og:type", "website", true);
    if (ogImg) setMeta("og:image", ogImg, true);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", desc);
    if (ogImg) setMeta("twitter:image", ogImg);
    // Auto-generated branded OG image per hunt (overrides Unsplash)
    if (h?.id) {
      const brandedOg = `${process.env.REACT_APP_BACKEND_URL}/api/hunts/${h.id}/og.png`;
      setMeta("og:image", brandedOg, true);
      setMeta("twitter:image", brandedOg);
    }
    // Canonical
    // Canonical follows the URL the visitor is on — both /citta/:city and
    // /caccia-al-tesoro/:city are legitimate entry points and can be indexed.
    const path = window.location.pathname;
    setLink("canonical", `${window.location.origin}${path}${IT ? "" : "?lang=en"}`);
    // Hreflang alternates so Google surfaces the right locale per query.
    setAltLink("alternate", `${window.location.origin}${path}`, "it");
    setAltLink("alternate", `${window.location.origin}${path}?lang=en`, "en");
    setAltLink("alternate", `${window.location.origin}${path}`, "x-default");
    // Structured data (JSON-LD)
    const structured = {
      "@context": "https://schema.org",
      "@type": h ? "TouristTrip" : "TouristDestination",
      name: h?.title || (IT ? `Cosa fare a ${cityTitle}` : `What to do in ${cityTitle}`),
      description: desc,
      touristType: "Cultural travellers",
      inLanguage: IT ? "it-IT" : "en-US",
      itinerary: h ? {
        "@type": "ItemList",
        itemListElement: (h.description ? [{ "@type": "ListItem", position: 1, name: h.title }] : []),
      } : undefined,
      geo: { "@type": "Place", name: cityTitle, addressCountry: "IT" },
      image: ogImg,
    };
    injectJsonLd(structured);
    return () => { removeJsonLd(); };
  }, [data, city, cityTitle, IT]);

  if (loading) return <PageShell><div className="text-muted-foreground italic py-16 text-center">{IT ? "Caricamento…" : "Loading…"}</div></PageShell>;
  if (!data) return <PageShell><div className="text-muted-foreground italic py-16 text-center">{IT ? "Città non trovata" : "City not found"}</div></PageShell>;

  const hunt = data.hunts?.[0];

  return (
    <PageShell>
      {/* Language toggle — floating pill top-right, persists to localStorage
          via useLang. Keeps user on the same URL, just re-fetches summary. */}
      <div className="fixed top-20 right-4 z-30 flex items-center gap-1 bg-white/90 backdrop-blur border border-border rounded-full shadow-sm px-1 py-1" data-testid="city-lang-toggle">
        <Languages className="w-3.5 h-3.5 ml-1.5 text-muted-foreground"/>
        <button
          onClick={() => setLang("it")}
          className={`text-[11px] uppercase tracking-widest px-2.5 py-1 rounded-full transition-all ${IT ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
          data-testid="city-lang-it"
        >IT</button>
        <button
          onClick={() => setLang("en")}
          className={`text-[11px] uppercase tracking-widest px-2.5 py-1 rounded-full transition-all ${!IT ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
          data-testid="city-lang-en"
        >EN</button>
      </div>

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="city-hero">
        <div className="absolute inset-0">
          <img src={hunt?.hero_image || data.properties?.[0]?.photos?.[0] || "https://images.unsplash.com/photo-1568650562100-0ef01fbbf5f2?w=1600"} alt="" className="w-full h-full object-cover"/>
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(35,31,27,0.35) 0%, rgba(35,31,27,0.85) 100%)" }}/>
        </div>
        <div className="relative max-w-4xl mx-auto px-6 py-32 sm:py-40 text-center text-white">
          <div className="text-xs uppercase tracking-[0.4em] opacity-80 mb-6 flex items-center justify-center gap-2">
            <MapPin className="w-4 h-4"/> {IT ? "Italia" : "Italy"}
          </div>
          <h1 className="font-display font-normal italic leading-[0.95] mb-6" style={{ fontSize: "clamp(3rem, 7vw, 6rem)" }}>
            {cityTitle}
          </h1>
          <p className="text-lg italic opacity-95 max-w-2xl mx-auto">
            {hunt ? hunt.subtitle : `${data.properties_total} ${IT ? "strutture ricettive · esperienze locali · storie da vivere" : "stays · local experiences · stories to live"}`}
          </p>
        </div>
      </section>

      {/* HUNT SECTION */}
      {hunt && (
        <section className="max-w-5xl mx-auto px-6 py-20" data-testid="city-hunt-section">
          {/* Preview banner — sets the expectation that stories, GPS and QR
              unlocks are gated behind a real check-in. Keeps the anonymous
              visitor on-page while giving them a clear conversion route. */}
          <div className="mb-10 border border-primary/30 bg-primary/5 rounded-md p-4 flex items-start gap-3" data-testid="city-preview-banner">
            <Lock className="w-5 h-5 text-primary shrink-0 mt-0.5"/>
            <div className="text-sm leading-relaxed">
              <div className="font-semibold text-primary mb-1">
                {IT ? "Anteprima pubblica della mappa" : "Public map preview"}
              </div>
              <p className="text-muted-foreground">
                {IT
                  ? <>Questa pagina mostra un assaggio della Mappa del Tesoro di {cityTitle}. L'esperienza reale — <b>storie complete, coordinate GPS, QR di sblocco e premio finale</b> — è riservata agli ospiti dopo il check-in in una struttura convenzionata.</>
                  : <>This page shows a taste of the {cityTitle} Treasure Hunt. The real experience — <b>full stories, GPS coordinates, unlock QR codes and the final prize</b> — is reserved for guests after check-in at a partner stay.</>}
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div className="relative aspect-[4/5] overflow-hidden rounded-sm shadow-xl">
              <img src={hunt.hero_image} alt="" className="tesoro-vintage w-full h-full object-cover hover:scale-105 transition-transform duration-700"/>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.35em] text-primary mb-3 font-semibold flex items-center gap-2">
                <Compass className="w-3 h-3"/> {IT ? "Mappa del Tesoro" : "Treasure Hunt"}
              </div>
              <h2 className="font-display font-normal italic mb-4" style={{ fontSize: "clamp(1.75rem, 3vw, 2.75rem)" }}>
                {hunt.title}
              </h2>
              <p className="text-lg font-display italic text-muted-foreground leading-relaxed mb-6">{hunt.subtitle}</p>
              {hunt.description && <p className="text-base leading-[1.8] mb-6" style={{ color: "#4A413A" }}>{hunt.description}</p>}
              {hunt.prize_title && (
                <div className="border-l-2 border-primary/50 pl-4 mb-6">
                  <div className="text-[10px] uppercase tracking-widest text-primary mb-1 flex items-center gap-1">
                    <Trophy className="w-3 h-3"/> {IT ? "Premio Finale" : "Final Prize"}
                  </div>
                  <div className="text-sm italic">{hunt.prize_title}</div>
                </div>
              )}
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={async () => {
                    const next = !previewOpen;
                    setPreviewOpen(next);
                    // Lazy-load the 3 teaser stops on first open; the /hunts
                    // endpoint already redacts story/lat/lng/qr_token for
                    // non-guest callers so what we receive here is safe to
                    // render as a public taste.
                    if (next && !previewStops) {
                      setPreviewLoading(true);
                      try {
                        const r = await api.get(`/hunts/${hunt.id}`);
                        const narratives = (r.data?.stops || []).filter((s) => s.path === "narrative");
                        setPreviewStops(narratives.slice(0, 3));
                      } catch { setPreviewStops([]); }
                      finally { setPreviewLoading(false); }
                    }
                    // Scroll the preview into view a beat after the DOM updates
                    if (next) setTimeout(() => {
                      document.getElementById("city-mini-preview")?.scrollIntoView({ behavior: "smooth", block: "start" });
                    }, 60);
                  }}
                  className="inline-flex items-center gap-2 text-sm px-5 py-3 rounded-full border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-all lift"
                  data-testid="city-hunt-preview-cta"
                >
                  <Sparkles className="w-4 h-4"/>
                  {previewOpen
                    ? (IT ? "Chiudi anteprima" : "Close preview")
                    : (IT ? "Vedi anteprima mappa" : "See map preview")}
                </button>
                <Link
                  to={`/strutture?city=${encodeURIComponent(city)}`}
                  className="inline-flex items-center gap-2 text-sm px-5 py-3 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-all lift"
                  data-testid="city-hunt-book-cta"
                >
                  <KeyRound className="w-4 h-4"/> {IT ? "Prenota per sbloccare" : "Book to unlock"}
                </Link>
              </div>
            </div>
          </div>

          {/* Counter — segreti / tavole / premio / sponsor. Drawn from the
              backend `hunt.stats` block so it stays true to the live map. */}
          {hunt.stats && (
            <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="city-hunt-counter">
              <CounterTile
                icon={ScrollText}
                value={hunt.stats.narratives || 0}
                label={IT ? "Segreti da svelare" : "Secrets to uncover"}
                testId="counter-secrets"
              />
              <CounterTile
                icon={UtensilsCrossed}
                value={hunt.stats.food_stops || 0}
                label={IT ? "Tavole d'autore" : "Signature tables"}
                testId="counter-food"
              />
              <CounterTile
                icon={Trophy}
                value={hunt.stats.has_prize ? 1 : 0}
                label={IT ? "Premio finale" : "Final prize"}
                testId="counter-prize"
                highlight={hunt.stats.has_prize}
              />
              <CounterTile
                icon={Handshake}
                value={hunt.stats.sponsors || 0}
                label={IT ? "Partner locali" : "Local partners"}
                testId="counter-sponsors"
              />
            </div>
          )}

          {/* Inline mini-preview — max 3 teaser tappe. Kept intentionally
              small and non-interactive: no QR, no GPS, no story text. */}
          {previewOpen && (
            <div id="city-mini-preview" className="mt-16 pt-10 border-t border-border" data-testid="city-mini-preview">
              <div className="text-center mb-8">
                <div className="text-[11px] uppercase tracking-[0.35em] text-primary mb-3 font-semibold">
                  {IT ? "Un assaggio della mappa" : "A taste of the map"}
                </div>
                <h3 className="font-display italic" style={{ fontSize: "clamp(1.5rem, 2.5vw, 2.25rem)" }}>
                  {IT ? "3 tappe in anteprima" : "3 stops as a teaser"}
                </h3>
                <p className="text-sm text-muted-foreground italic mt-2 max-w-lg mx-auto">
                  {IT
                    ? <>Le storie complete, le coordinate GPS e i QR di sblocco appaiono dopo il check-in in una struttura di {cityTitle}.</>
                    : <>Full stories, GPS coordinates and unlock QR codes appear after check-in at a {cityTitle} stay.</>}
                </p>
              </div>

              {previewLoading && (
                <div className="text-center py-8 italic text-muted-foreground">
                  {IT ? "Carico l'anteprima…" : "Loading preview…"}
                </div>
              )}

              {!previewLoading && previewStops && previewStops.length > 0 && (
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {previewStops.map((s, i) => (
                    <div key={s.id} className="border border-border rounded-md overflow-hidden bg-card shadow-sm flex flex-col" data-testid={`city-preview-stop-${s.id}`}>
                      <div className="relative aspect-[4/3] bg-muted overflow-hidden">
                        {s.hero_image ? (
                          <img src={s.hero_image} alt={s.title} className="tesoro-vintage w-full h-full object-cover"/>
                        ) : (
                          <div className="w-full h-full grid place-items-center text-muted-foreground italic text-xs">
                            {IT ? "Illustrazione in arrivo" : "Illustration coming"}
                          </div>
                        )}
                        <span className="absolute top-2 left-2 text-[9px] uppercase tracking-widest bg-primary text-primary-foreground px-2 py-0.5 rounded">
                          {IT ? `Tappa ${i + 1}` : `Stop ${i + 1}`}
                        </span>
                      </div>
                      <div className="p-4 flex-1 flex flex-col gap-2">
                        <div className="font-display italic text-lg leading-tight">{s.title}</div>
                        <p className="text-sm text-muted-foreground italic leading-relaxed">{s.teaser}</p>
                        <div className="mt-auto pt-2 flex items-center gap-1.5 text-[11px] text-primary/80">
                          <Lock className="w-3 h-3"/>
                          <span>{IT ? "Storia completa dopo il check-in" : "Full story after check-in"}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {!previewLoading && previewStops && previewStops.length === 0 && (
                <div className="text-center py-8 italic text-muted-foreground text-sm">
                  {IT ? "Anteprima non disponibile per questa mappa." : "Preview not available for this map."}
                </div>
              )}

              {hunt.stats && (previewStops?.length || 0) > 0 && (
                <div className="text-center mt-8">
                  <div className="text-xs text-muted-foreground italic mb-3">
                    {IT
                      ? <>Anteprima di 3 tappe su {hunt.stats.secrets_total}. Le rimanenti si sbloccano prenotando.</>
                      : <>3 stops previewed out of {hunt.stats.secrets_total}. The rest unlock when you book.</>}
                  </div>
                  <Link
                    to={`/strutture?city=${encodeURIComponent(city)}`}
                    className="inline-flex items-center gap-2 text-sm px-6 py-3 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-all lift"
                    data-testid="city-preview-unlock-cta"
                  >
                    <KeyRound className="w-4 h-4"/> {IT ? "Prenota per sbloccare la mappa completa" : "Book to unlock the full map"}
                  </Link>
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {/* PROPERTIES SECTION */}
      {data.properties?.length > 0 && (
        <section className="border-t border-border" style={{ background: "#EDE4D2" }}>
          <div className="max-w-6xl mx-auto px-6 py-20" data-testid="city-properties-section">
            <div className="text-center mb-12">
              <div className="text-[11px] uppercase tracking-[0.35em] text-primary mb-3 font-semibold">{IT ? "Dove dormire" : "Where to stay"}</div>
              <h2 className="font-display italic" style={{ fontSize: "clamp(2rem, 4vw, 3.25rem)" }}>
                {data.properties_total} {IT ? `strutture a ${cityTitle}` : `stays in ${cityTitle}`}
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {data.properties.map((p) => (
                <Link key={p.id} to={`/p/${p.slug}`} className="group block relative aspect-[4/5] overflow-hidden rounded-sm shadow-md" data-testid={`city-property-${p.slug}`}>
                  <img src={absUrl(p.hero_image || p.photos?.[0])} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
                  <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(35,31,27,0.05) 40%, rgba(35,31,27,0.85) 100%)" }}/>
                  <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                    <div className="text-[10px] uppercase tracking-widest opacity-80">{p.property_type}</div>
                    <div className="font-display italic text-2xl leading-tight">{p.name}</div>
                    <div className="text-xs mt-3 opacity-70 group-hover:opacity-100 group-hover:translate-x-1 transition-all inline-flex items-center gap-1">
                      {IT ? "Vedi struttura" : "View stay"} <ArrowUpRight className="w-3 h-3"/>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            {data.properties_total > data.properties.length && (
              <div className="text-center mt-8">
                <Link to={`/strutture?city=${encodeURIComponent(city)}`} className="text-sm text-primary underline underline-offset-4">
                  {IT ? `Vedi tutte le ${data.properties_total} strutture a ${cityTitle}` : `See all ${data.properties_total} stays in ${cityTitle}`}
                </Link>
              </div>
            )}
          </div>
        </section>
      )}
      {/* EDITORIAL SEO SECTION — always at the bottom, feeds Google with 3
          paragraphs of genuine editorial copy (AI-generated on first hit and
          cached forever). If the AI is unavailable the endpoint returns a
          human-readable fallback so the page never looks empty. */}
      {data.editorial?.paragraphs?.length > 0 && (
        <section className="border-t border-border bg-background" data-testid="city-editorial">
          <div className="max-w-3xl mx-auto px-6 py-20">
            <div className="text-center mb-10">
              <div className="text-[11px] uppercase tracking-[0.35em] text-primary mb-3 font-semibold">
                {IT ? `L'essenza di ${cityTitle}` : `The essence of ${cityTitle}`}
              </div>
              <h2 className="font-display italic" style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}>
                {IT ? "Perché fermarti qui" : "Why stay here"}
              </h2>
            </div>
            <article className="prose prose-lg max-w-none space-y-5" style={{ color: "#3a2a17" }}>
              {data.editorial.paragraphs.map((p, i) => (
                <p key={i} className="text-base leading-[1.9]" style={{ fontSize: "17px" }} data-testid={`city-editorial-p${i}`}>
                  {p}
                </p>
              ))}
            </article>
            {data.editorial.source === "ai" && (
              <div className="text-[10px] text-muted-foreground italic mt-8 text-center">
                {IT ? "· Ritratto della città generato da IA e curato dalla redazione ·" : "· City portrait AI-generated and editorially curated ·"}
              </div>
            )}
          </div>
        </section>
      )}
    </PageShell>
  );
}

function PageShell({ children }) {
  return (
    <div className="min-h-screen bg-background">
      <Navbar/>
      {children}
    </div>
  );
}

// Small square tile used by the CityPage counter. Kept module-level so the
// parent's re-renders (e.g. lang toggle) don't remount it needlessly.
function CounterTile({ icon: Icon, value, label, testId, highlight = false }) {
  return (
    <div
      className={`border rounded-md p-4 text-center transition-colors ${highlight ? "border-primary/50 bg-primary/5" : "border-border bg-card"}`}
      data-testid={testId}
    >
      <Icon className={`w-6 h-6 mx-auto mb-2 ${highlight ? "text-primary" : "text-muted-foreground"}`}/>
      <div className={`font-display italic text-3xl leading-none ${highlight ? "text-primary" : ""}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1.5">{label}</div>
    </div>
  );
}

// ---------- SEO meta helpers ----------
function setMeta(name, content, isOg = false) {
  const sel = isOg ? `meta[property="${name}"]` : `meta[name="${name}"]`;
  let el = document.querySelector(sel);
  if (!el) {
    el = document.createElement("meta");
    if (isOg) el.setAttribute("property", name); else el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]:not([hreflang])`);
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
  el.setAttribute("href", href);
}
function setAltLink(rel, href, hreflang) {
  let el = document.querySelector(`link[rel="${rel}"][hreflang="${hreflang}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}
function injectJsonLd(obj) {
  removeJsonLd();
  const s = document.createElement("script");
  s.type = "application/ld+json";
  s.id = "city-jsonld";
  s.text = JSON.stringify(obj);
  document.head.appendChild(s);
}
function removeJsonLd() {
  const s = document.getElementById("city-jsonld");
  if (s) s.remove();
}
